import React, { useState, useEffect } from 'react';
import { Paper, Box, TextField, Grid, Typography, Button, Card, CardContent, IconButton, Alert, InputAdornment } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Add as AddIcon, Delete as DeleteIcon, Image as ImageIcon } from '@mui/icons-material';
import SitesService from '../../../services/SitesService';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import { useSitesConfig } from '../SitesContext';
import { prepareFieldUpdates } from '../utils/fieldUtils';
import ImageSelector from '../components/ImageSelector';

const isImagePath = (value) => value && typeof value === 'string' && !value.startsWith('http');

const LocationsTab = ({ leadingContent }) => {
  const { t } = useTranslation();
  const { config, handleChange, handleAddArrayItem, handleArrayChange, handleRemoveArrayItem, isFieldConfigurable, isFieldFixed, loadConfig } = useSitesConfig();
  const [saving, setSaving] = useState(false);
  const [imageSelectorOpen, setImageSelectorOpen] = useState(false);
  const [imageSelectorIndex, setImageSelectorIndex] = useState(null);
  const [previewUrls, setPreviewUrls] = useState({});

  const locationsPath = 'siteConfig.locations';
  const locationsItemsPath = 'siteConfig.locations.items';
  const isTitleConfigurable = isFieldConfigurable(`${locationsPath}.title`);
  const isSubtitleConfigurable = isFieldConfigurable(`${locationsPath}.subtitle`);
  const isItemsConfigurable = isFieldConfigurable(locationsItemsPath);
  const isItemsFixed = isFieldFixed(locationsItemsPath);
  
  const handleOpenImageSelector = (index) => {
    setImageSelectorIndex(index);
    setImageSelectorOpen(true);
  };

  const handleImageSelect = (imageUrl) => {
    if (imageSelectorIndex !== null) {
      const newItems = [...(config.siteConfig.locations.items || [])];
      newItems[imageSelectorIndex] = {
        ...newItems[imageSelectorIndex],
        image: imageUrl
      };
      handleChange('siteConfig.locations.items', newItems);
    }
    setImageSelectorOpen(false);
    setImageSelectorIndex(null);
  };

  const locationImages = config?.siteConfig?.locations?.items?.map((loc) => loc?.image).filter(Boolean) || [];
  const imagePaths = locationImages.filter(isImagePath);
  const pathsKey = JSON.stringify(imagePaths);
  useEffect(() => {
    if (imagePaths.length === 0) {
      setPreviewUrls({});
      return;
    }
    let cancelled = false;
    const load = async () => {
      const next = {};
      for (const p of imagePaths) {
        if (cancelled) return;
        const url = await SitesService.getImagePreviewUrl(p);
        if (!cancelled && url) next[p] = url;
      }
      if (!cancelled) setPreviewUrls((prev) => ({ ...prev, ...next }));
    };
    load();
    return () => { cancelled = true; };
  }, [pathsKey]);

  const getPreviewSrc = (value) => {
    if (!value) return null;
    if (!isImagePath(value)) return value;
    return previewUrls[value] || null;
  };

  const hasEditableFields = isTitleConfigurable || isSubtitleConfigurable || (isItemsConfigurable && !isItemsFixed);
  
  if (!hasEditableFields) {
    return (
      <Paper sx={{ padding: 3, mt: 2 }}>
        <Alert severity="info">
          {t('no_editable_fields_in_section') || 'No hay campos editables en esta sección para el template actual'}
        </Alert>
      </Paper>
    );
  }

  const handleSave = async () => {
    try {
      setSaving(true);

      // Preparar updates para campos configurables de locations
      const updates = {};
      
      // Título y subtítulo
      if (isTitleConfigurable) {
        updates[`${locationsPath}.title`] = config.siteConfig.locations.title;
      }
      if (isSubtitleConfigurable) {
        updates[`${locationsPath}.subtitle`] = config.siteConfig.locations.subtitle;
      }
      
      // Items del array
      if (isItemsConfigurable && !isItemsFixed) {
        updates[locationsItemsPath] = config.siteConfig.locations.items;
      }

      if (Object.keys(updates).length === 0) {
        SnackbarUtils.warning(t('no_editable_fields') || 'No hay campos editables en esta sección');
        return;
      }

      const preparedUpdates = prepareFieldUpdates(updates);
      await SitesService.updateFields(preparedUpdates);
      await loadConfig();
      
      SnackbarUtils.success(t('section_saved_successfully') || 'Sección guardada exitosamente');
    } catch (err) {
      console.error('Error saving locations:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message;
      SnackbarUtils.error(errorMessage || (t('error_saving_section') || 'Error al guardar la sección'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ padding: 3, mt: 2 }}>
      {leadingContent}
      <Grid container spacing={2}>
        {(isTitleConfigurable || isSubtitleConfigurable) && (
          <>
            {isTitleConfigurable && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('locations_title') || 'Título'}
                  value={config.siteConfig.locations.title || ''}
                  onChange={(e) => handleChange('siteConfig.locations.title', e.target.value)}
                />
              </Grid>
            )}
            {isSubtitleConfigurable && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('locations_subtitle') || 'Subtítulo'}
                  value={config.siteConfig.locations.subtitle || ''}
                  onChange={(e) => handleChange('siteConfig.locations.subtitle', e.target.value)}
                />
              </Grid>
            )}
          </>
        )}
        {isItemsConfigurable && !isItemsFixed && (
          <>
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => handleAddArrayItem('siteConfig.locations.items', {
                    id: Date.now().toString(),
                    name: '',
                    address: '',
                    phone: '',
                    email: '',
                    hours: '',
                    image: '',
                    mapUrl: null
                  })}
                  variant="outlined"
                >
                  {t('add_location') || 'Agregar Ubicación'}
                </Button>
              </Box>
            </Grid>
            {config.siteConfig.locations.items?.map((location, index) => (
              <Grid item xs={12} key={location.id || index}>
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="subtitle1">{t('location') || 'Ubicación'} {index + 1}</Typography>
                      <IconButton
                        onClick={() => handleRemoveArrayItem('siteConfig.locations.items', index)}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label={t('location_name') || 'Nombre'}
                          value={location.name || ''}
                          onChange={(e) => handleArrayChange('siteConfig.locations.items', index, 'name', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label={t('address') || 'Dirección'}
                          value={location.address || ''}
                          onChange={(e) => handleArrayChange('siteConfig.locations.items', index, 'address', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label={t('phone') || 'Teléfono'}
                          value={location.phone || ''}
                          onChange={(e) => handleArrayChange('siteConfig.locations.items', index, 'phone', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label={t('email') || 'Email'}
                          type="email"
                          value={location.email || ''}
                          onChange={(e) => handleArrayChange('siteConfig.locations.items', index, 'email', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label={t('hours') || 'Horarios'}
                          value={location.hours || ''}
                          onChange={(e) => handleArrayChange('siteConfig.locations.items', index, 'hours', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                          {(location.image && (getPreviewSrc(location.image) || !isImagePath(location.image))) && (
                            <Box sx={{ flexShrink: 0 }}>
                              <img
                                src={getPreviewSrc(location.image) || location.image}
                                alt={`Preview ${index + 1}`}
                                style={{
                                  maxWidth: '200px',
                                  maxHeight: '200px',
                                  objectFit: 'contain',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px',
                                  padding: '8px'
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            </Box>
                          )}
                          <TextField
                            fullWidth
                            sx={{ flex: 1, minWidth: 200 }}
                            label={t('image_url') || 'URL de Imagen'}
                            value={location.image || ''}
                            onChange={(e) => handleArrayChange('siteConfig.locations.items', index, 'image', e.target.value)}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => handleOpenImageSelector(index)}
                                    edge="end"
                                    color="primary"
                                  >
                                    <ImageIcon />
                                  </IconButton>
                                </InputAdornment>
                              )
                            }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </>
        )}
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
          {saving ? (t('saving') || 'Guardando...') : (t('save') || 'Guardar')}
        </Button>
      </Box>
      <ImageSelector
        open={imageSelectorOpen}
        onClose={() => {
          setImageSelectorOpen(false);
          setImageSelectorIndex(null);
        }}
        onSelect={handleImageSelect}
        currentValue={
          imageSelectorIndex !== null
            ? (config.siteConfig.locations.items?.[imageSelectorIndex]?.image || '')
            : ''
        }
        label={t('select_location_image') || 'Seleccionar Imagen de Ubicación'}
      />
    </Paper>
  );
};

export default LocationsTab;
